import React, { Fragment, useCallback, useEffect, useState } from "react"
import { useHistory, useParams } from "react-router-dom"
import { toast } from "react-toastify"
import Button from "../../../components/Button"
import { ENDPOINT, IMAGE_KIT_ENDPOINT_URL, Routes } from "../../../constants"
import { useApi } from "../../../contexts/ApiContext"
import { UserMetadata, ActionType } from "../../../types"
import { useAuth } from "../../../contexts/AuthContext"
import { InputField } from "../../../components/InputField"
import ThumbnailViewer from "../../../components/ThumbnailViewer"
import { getUtcEpoch } from "../../../utils/getUtcEpoch"
import { Notification } from "../../../components/Notification"
import CollectionActionForm from "../../../components/CollectionActionForm"
import { ActionDialog } from "../../../components/ActionDialog"
import { FetchApi } from "../../../api/fetch"

type ActionNote = {
    buttonText: string
    title: string
    actionType: ActionType
    values?: any
}

type Params = { collectionId?: string }
const MintCalendar = () => {
    const { push } = useHistory()
    const { collectionId } = useParams<Params>()
    const {
        retrieveDrafts,
        draftCollections,
        retrieveMintCalendar
    } = useApi()

    const { getUserJWT, currentUserMetadata, setIsLoadingOverlayDisplay, isCurrentUserAdmin } = useAuth()
    const isAdmin = isCurrentUserAdmin();

    const [actionNote, setActionNote] = useState<ActionNote | undefined>()
    const [mintCalendarEntry, setMintCalendarEntry] = useState<any>({
        mint_time: "",
        supply: "",
        mintPrice: "",
        id: "0",
    })
    const [collectionThumbnail] = useState<string>(String)
    const [collectionThumbnailUrl, setCollectionThumbnailUrl] = useState<string>()

    useEffect(() => {
        onRefresh()
    }, [])

    const onRefresh = useCallback(async () => {
        setIsLoadingOverlayDisplay(true)
        if (collectionId) {
            // use collectionid to get exact draft and update mint calendar entry details
            const mintEntry: any = await retrieveMintCalendar({ id: collectionId })
            if (mintEntry?.length > 0) {
                const entry = mintEntry[0]
                const t = entry.launchDate.split('Z')[0]
                setMintCalendarEntry({
                    ...mintCalendarEntry,
                    ...entry,
                    mint_time: t,
                })
            }

        } else {
            await retrieveDrafts(
                (await getUserJWT()) as string,
                currentUserMetadata as UserMetadata,
                { creatorId: currentUserMetadata?.userId }
            )
        }
        await handleThumbnail()

        setIsLoadingOverlayDisplay(false)

    }, [getUserJWT])

    useEffect(() => {
        handleThumbnail()
    }, [mintCalendarEntry])


    const handleChange = async (e: any) => {
        const name = e.target.name;
        const val = e.target.value

        let mintEntry;
        if (name === "collectionId") {
            // [...draftCollections, ...launchPads]
            mintEntry = draftCollections?.filter((item: any) => item.id === val)[0]
            setMintCalendarEntry({
                ...mintCalendarEntry,
                ...mintEntry
            })
        } else if (name === "mint_time") {
            setMintCalendarEntry((prev: any) => { return { ...prev, [name]: val } })
        } else if (name === "supply") {
            if (!isNaN(val) && val >= 0 && val % 1 === 0) {
                setMintCalendarEntry((prev: any) => { return { ...prev, [name]: Math.ceil(val) } })
            }
        }
        else if (name == "mintPrice" || name == "price") {
            if (!isNaN(val) && val >= 0) {
                setMintCalendarEntry((prev: any) => { return { ...prev, [name]: val } })
            }
        } else {
            setMintCalendarEntry((prev: any) => { return { ...prev, [name]: val } })
        }
    }

    const handleSubmit = async (e: any) => {
        e.preventDefault()
        if (+mintCalendarEntry?.supply <= 0 || +mintCalendarEntry?.mintPrice < 0) {
            toast.error(
                <Notification
                    title="Invalid Input"
                    description="Please enter a valid value for mint supply and price"
                />
            )
            return
        }
        // if (new Date(mintCalendarEntry?.mint_time) < getPreviousDay(new Date())) {
        //     toast.error(
        //         <Notification
        //             title="Invalid Date"
        //             description="Mint time cannot be a past date"
        //         />
        //     )
        //     return
        // }

        const JWT = JSON.parse(sessionStorage.getItem('userData') || '{}').token

        const mintDateEpoch = getUtcEpoch(String(mintCalendarEntry?.mint_time))
        setIsLoadingOverlayDisplay(true)

        try {
            let response = await FetchApi({
                endpoint: `${ENDPOINT.BASE_ENDPOINT}/${mintCalendarEntry.id}/mint_calendar`,
                method: "POST",
                JWT,
                body:  ({
                    ...mintCalendarEntry,
                    mint_time: mintDateEpoch,
                    supply: +mintCalendarEntry?.supply,
                    mintPrice: +mintCalendarEntry?.mintPrice
                }),
            })
            response && setIsLoadingOverlayDisplay(false)
            push(Routes.MINT_CALENDARS)
        } catch (error) {
            setIsLoadingOverlayDisplay(false)
        }


    }

    const handleRequestApproval = async (e: any) => {
        e.preventDefault()
        if (+mintCalendarEntry?.supply <= 0 || +mintCalendarEntry?.mintPrice < 0) {
            toast.error(
                <Notification
                    title="Invalid Input"
                    description="Please enter a valid value for mint supply and price"
                />
            )
            return
        }

        const JWT = await getUserJWT()

        const mintDateEpoch = getUtcEpoch(String(mintCalendarEntry?.mint_time))
        setIsLoadingOverlayDisplay(true)

        try {
            let response = await FetchApi({
                endpoint: `${ENDPOINT.BASE_ENDPOINT}/mint_calendar/${mintCalendarEntry.id}/request_verification`,
                method: "PUT",
                JWT,
                body:  ({
                    ...mintCalendarEntry,
                    mint_time: mintDateEpoch,
                    supply: +mintCalendarEntry?.supply,
                    mintPrice: +mintCalendarEntry?.mintPrice
                }),
            })
            response && setIsLoadingOverlayDisplay(false)
            push(Routes.MINT_CALENDARS)
        } catch (error) {
            setIsLoadingOverlayDisplay(false)
        }


    }

    const handleUpdate = async (e: any) => {
        e.preventDefault()

        if (+mintCalendarEntry?.supply < 0 || +mintCalendarEntry?.price < 0) {
            toast.error(
                <Notification
                    title="Invalid Input"
                    description="Mint supply or price cannot be negative numbers"
                />
            )
            return
        }

        const JWT = await getUserJWT()

        const mintDateEpoch = getUtcEpoch(String(mintCalendarEntry?.mint_time))

        setIsLoadingOverlayDisplay(true)

        try {

            let response = await FetchApi({
                endpoint: `${ENDPOINT.BASE_ENDPOINT}/${mintCalendarEntry.id}/mint_calendar`,
                method: "PUT",
                JWT,
                body:  ({
                    ...mintCalendarEntry,
                    mint_time: mintDateEpoch,
                    supply: Math.ceil(+mintCalendarEntry?.supply),
                    mintPrice: +mintCalendarEntry?.price,
                    price: +mintCalendarEntry?.price
                }),
            })
            response && setIsLoadingOverlayDisplay(false)
            push(Routes.MINT_CALENDARS)
        } catch (error) {
            setIsLoadingOverlayDisplay(false)

        }

    }
    const handleThumbnail = async () => {
        // setting up thumbnail  
        if (mintCalendarEntry?.thumbnail?.includes("thumbnails/")) {
            setCollectionThumbnailUrl(
                `${IMAGE_KIT_ENDPOINT_URL}${mintCalendarEntry?.thumbnailUrl}`
            )
        } else {
            setCollectionThumbnailUrl(mintCalendarEntry?.thumbnailUrl)
        }
    }
    const onReject = async () => {
        setActionNote({
            buttonText: "SEND REJECTION",
            title: "Rejection notes",
            actionType: ActionType.reject,
        })

    }
    const onDelete = async () => {
        setActionNote({
            buttonText: "DELETE ",
            title: "Deletion notes",
            actionType: ActionType.delete,
        })
    }

    const onApprove = async () => {
        setActionNote({
            buttonText: "APPROVE COLLECTION",
            title: "Approval notes",
            actionType: ActionType.approve,
        })
    }
    const respondToCollection = async (
        actionType: ActionType,
        notes: string,
        values?: AnalyserOptions
    ) => {
        setIsLoadingOverlayDisplay(true)

        const JWT = await getUserJWT()

        const endpoint = `${ENDPOINT.RESPOND_TO_COLLECTION}/${collectionId}/respond_to_mint_calendar`
        const mintDateEpoch = getUtcEpoch(String(mintCalendarEntry?.mint_time))

        if (actionType === ActionType.reject || actionType === ActionType.approve) {
            try {
                let response: any = await FetchApi({
                    endpoint,
                    method: "POST",
                    JWT,
                    body:  ({
                        ...mintCalendarEntry,
                        rejection_note: notes,
                        approve: actionType === ActionType.reject ? false : true,
                        mint_time: mintDateEpoch,
                        supply: +mintCalendarEntry?.supply,
                        mintPrice: +mintCalendarEntry?.price,
                        price: +mintCalendarEntry?.price
                    })
                })
                response && setIsLoadingOverlayDisplay(false)
                push(Routes.MINT_CALENDARS)
            } catch (err) {
                console.log(err)
                toast.error(
                    <Notification
                        title="Error responding to collection"
                        description=""
                    />
                )
            }

        }
        if (actionType === ActionType.delete) {
            try {
                let response: any = await FetchApi({
                    endpoint: `${ENDPOINT.DELETE_LIVE_COLLECTION}/${collectionId}/mint_calendar`,
                    method: "DELETE",
                    JWT,
                    body:  ({ deletionNote: notes })
                })
                response && setIsLoadingOverlayDisplay(false)
                push(Routes.MINT_CALENDARS)
            } catch (err) {
                console.log(err)
                toast.error(
                    <Notification
                        title="Error deleting collection"
                        description="Unknown error deleting collection."
                    />
                )
            }
        }
        setIsLoadingOverlayDisplay(false)
    }
    const handleClose = () => {
        setActionNote(undefined)
    }
    // , ...launchPads
    const mapped = collectionId ? [mintCalendarEntry] : [...draftCollections]

    return (
        <div className="flex flex-col w-11/12 mx-auto pb-2">
            <div >
                <form
                    onSubmit={handleSubmit}
                    className="w-full mb-48"
                >
                    <div className="mt-8 flex flex-row mb-4 justify-between items-center sm:flex-row">
                        <div className="mb-4 sm:mb-0">
                            <h1 className="font-semibold text-3xl text-left">
                                {isAdmin ? "REVIEW MINT CALENDAR SUBMISSION" : "SUBMIT NEW UPCOMING MINT"}
                            </h1>
                        </div>
                        <div className="w-full sm:w-auto flex items-center space-x-4">
                            {
                                isAdmin && collectionId &&
                                <Fragment>
                                    {
                                        !mintCalendarEntry?.isApproved &&
                                        <Fragment>
                                            <Button
                                                type="button"
                                                className="mb-0 sm:mb-2 w-auto btn"
                                                onClick={onApprove}
                                            >
                                                APPROVE
                                            </Button>
                                            <Button
                                                type="button"
                                                className="mb-0 sm:mb-2 w-auto btn btn-red"
                                                onClick={onReject}
                                            >
                                                REJECT
                                            </Button>
                                        </Fragment>

                                    }

                                </Fragment>
                            }

                            <Fragment>
                                {
                                    collectionId ?
                                        <Fragment>
                                            {
                                                mintCalendarEntry.isRejected &&
                                                <Button
                                                    type="button"
                                                    className="mb-0 sm:mb-2 w-auto btn bg-kyzzen-secondary text-gray-100 font-medium"
                                                    onClick={handleRequestApproval}
                                                >
                                                    Request Approval
                                                </Button>
                                            }
                                            <Button
                                                type="button"
                                                className="mb-0 sm:mb-2 w-auto btn bg-gradient text-kyzzen-primary font-medium"
                                                onClick={handleUpdate}
                                            >
                                                UPDATE
                                            </Button>
                                            <Button
                                                type="button"
                                                className="mb-0 sm:mb-2 w-auto btn btn-gray"
                                                onClick={onDelete}
                                            >
                                                DELETE
                                            </Button>
                                        </Fragment> :
                                        <Button
                                            type="submit"
                                            className="mb-0 sm:mb-2 w-auto btn bg-gradient text-kyzzen-primary font-medium"
                                        >
                                            SUBMIT
                                        </Button>
                                }
                            </Fragment>
                        </div>
                    </div>
                    {
                        mintCalendarEntry.id == "0" &&
                        <div className="mb-4 mt-12">
                            <select name="collectionId" className="w-full h-12 bg-black text-gray-200 border border-gray-400 border-box px-2 rounded-lg" disabled={Boolean(collectionId)} onChange={handleChange} value={mintCalendarEntry.collectionId} >
                                <option value="0">Select An Approved Launch or Draft Collection To Load Details...</option>
                                {
                                    mapped?.map((item: any) => (
                                        <option className="bg-black text-sm text-gray-200" style={{ color: "white" }} key={item.id} value={item.id} >{item.name}</option>
                                    ))
                                }
                            </select>
                        </div>
                    }

                    {
                        mintCalendarEntry.id !== "0" &&
                        <Fragment>
                            <div className="mb-4">
                                <InputField
                                    onChange={handleChange}
                                    value={mintCalendarEntry.name}
                                    required
                                    className="w-full"
                                    name="name"
                                    label="Collection Name"
                                />
                            </div>
                            <div className="mb-4">
                                <InputField
                                    onChange={handleChange}
                                    value={mintCalendarEntry.description}
                                    required
                                    className="w-full"
                                    name="description"
                                    label="Collection Description"
                                />
                            </div>
                            <div className="mb-4">
                                <InputField
                                    onChange={handleChange}
                                    value={mintCalendarEntry.website}
                                    className="w-full"
                                    name="website"
                                    label="Website Link"
                                />
                            </div>
                            <div className="mb-4">
                                <InputField
                                    onChange={handleChange}
                                    value={mintCalendarEntry.discord}
                                    className="w-full"
                                    name="discord"
                                    label="Discord Link"
                                />
                            </div>
                            <div className="mb-4">
                                <InputField
                                    onChange={handleChange}
                                    value={mintCalendarEntry.twitter}
                                    required
                                    className="w-full"
                                    name="twitter"
                                    label="Twitter Link"
                                    readOnly
                                />
                            </div>
                            <div className="mb-4 flex gap-2 w-full">
                                <InputField
                                    onChange={handleChange}
                                    value={mintCalendarEntry.subberPresaleUrl}
                                    className="w-full"
                                    name="subberPresaleUrl"
                                    label="Subber Presale Link"
                                />
                                <InputField
                                    onChange={handleChange}
                                    value={mintCalendarEntry.atlas3PresaleUrl}
                                    className="w-full"
                                    name="atlas3PresaleUrl"
                                    label="Atlas3 Presale Link"
                                />
                            </div>
                            <div className="mb-4 flex gap-2 w-full">
                                <InputField
                                    onChange={handleChange}
                                    value={mintCalendarEntry.subberGiveawayUrl}
                                    className="w-full"
                                    name="subberGiveawayUrl"
                                    label="Subber Giveaway Link"
                                />
                                <InputField
                                    onChange={handleChange}
                                    value={mintCalendarEntry.atlas3GiveawayUrl}
                                    className="w-full"
                                    name="atlas3GiveawayUrl"
                                    label="Atlas3 Giveaway Link"
                                />
                            </div>
                            <hr className="border border-gray-700 my-10" />
                            <div className="mb-4">
                                <span className="text-sm text-gray-40 block mb-2">MINT DATE AND UTC TIME</span>
                                <input
                                    type="datetime-local"
                                    value={mintCalendarEntry?.mint_time}
                                    // defaultValue={mintCalendarEntry?.mint_time}
                                    className="block text-sm text-left font-base text-gray-40 mb-2 w-full bg-transparent h-12 rounded-lg border-2 border-kyzzen-secondary px-3"
                                    required
                                    name="mint_time"
                                    onChange={handleChange}
                                    min="2022-10-17"
                                />
                            </div>
                            <div className="mb-4">
                                <InputField
                                    type="text"
                                    onChange={handleChange}
                                    value={mintCalendarEntry.supply}
                                    required
                                    name="supply"
                                    label="Mint Supply"
                                    className="w-full"
                                    warning={{
                                        isVisible: Boolean(mintCalendarEntry.supply <= 0),
                                        text: "supply should be non-negative and non-decimal"
                                    }}
                                />

                            </div>
                            <div className="mb-4">
                                <InputField
                                    type="text"
                                    onChange={handleChange}
                                    value={collectionId ? mintCalendarEntry.price : mintCalendarEntry.mintPrice}
                                    required
                                    className="w-full"
                                    name={`${collectionId ? "price" : "mintPrice"}`}
                                    label="Mint Price (SOL)"
                                // warning={{
                                //     isVisible: Boolean(!isNaN(+mintCalendarEntry.mintPrice) || !isNaN(+mintCalendarEntry.price) || +mintCalendarEntry.mintPrice < 0 || +mintCalendarEntry.price < 0),
                                //     text: "please provide a non-negative value"
                                // }}

                                />
                            </div>
                            <hr className="border border-gray-700 my-10" />
                            <div className="mb-4">
                                <ThumbnailViewer
                                    iconSrc={
                                        Boolean(collectionThumbnailUrl && !collectionThumbnail)
                                            ? collectionThumbnailUrl
                                            : collectionThumbnail
                                    }
                                />

                            </div>
                        </Fragment>
                    }
                </form>


            </div>
            {actionNote && (
                <ActionDialog
                    isOpen={!!actionNote}
                    onCloseClick={handleClose}
                    heading={actionNote.title}
                >
                    <CollectionActionForm
                        actionButtonText={actionNote.buttonText}
                        actionType={actionNote.actionType}
                        values={actionNote?.values}
                        onSubmit={respondToCollection}
                    />
                </ActionDialog>
            )}
        </div >
    )
}

export default MintCalendar
